import { sort, uniqBy } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  cache,
  revalidateData,
  updateFetcherData,
  useFetcherData,
  useRequest,
} from "@prismicio/editor-support/Suspense"
import { type MediaAssetType } from "@prismicio/editor-ui"
import { useState } from "react"
import { z } from "zod"

import {
  type AuthStrategy,
  type EditorConfig,
  throwConfigError,
  useEditorConfig,
} from "../../../EditorConfig"
import { createSearchParams } from "../../service"
import { tagSchema } from "./tagData"
import { useMediaLibraryAnalyticsMediaSearch } from "./useMediaLibraryAnalytics"

type MediaSearchApiParams =
  | "keyword"
  | "assetType"
  | "uploaderId"
  | "tags"
  | "cursor"
  | "limit"

const highlightType = z
  .array(z.string())
  .optional()
  .transform((arr) => arr?.[0])

export const mediaAssetType = z.object({
  id: z.string(),
  kind: z.string(),
  extension: z.string().optional(),
  filename: z.string(),
  width: z.number().optional(),
  height: z.number().optional(),
  last_modified: z.number(),
  url: z.string(),
  alt: z.string().optional(),
  uploader_id: z.string().optional(),
  // file size in bytes
  size: z.number(),
  notes: z.string().optional(),
  credits: z.string().optional(),
  search_highlight: z
    .object({
      filename: highlightType,
      alt: highlightType,
      notes: highlightType,
      credits: highlightType,
    })
    .optional(),
  tags: z.array(tagSchema),
})

export const assetApiHeaders = {
  "X-Prismic-Source": "Wroom", // Tracking purpose
}

export const mediaSearchResponse = z.object({
  total: z.number(),
  cursor: z.string().optional(),
  items: z.array(mediaAssetType),
})

const limit = 30

export type MediaAsset = z.TypeOf<typeof mediaAssetType>

export interface ExternalImage {
  id: string
  url: string
  width: number
  height: number
  alt?: string
}

export type MediaAssetOrExternalImage = MediaAsset | ExternalImage

export function searchMedia(
  baseUrl: URL | undefined,
  repository: string,
  authStrategy: AuthStrategy,
  keyword: string,
  assetType: MediaAssetType,
  uploaderId: string | undefined,
  tags: string,
  page: number,
  cursor?: string,
) {
  const requestUrl = baseUrl ? new URL("assets", baseUrl) : undefined
  const query = new URLSearchParams(
    createSearchParams<MediaSearchApiParams>([
      { name: "limit", values: [`${limit}`] },
      { name: "cursor", values: [cursor] },
      { name: "keyword", values: [keyword] },
      { name: "assetType", values: [assetType] },
      { name: "uploaderId", values: [uploaderId] },
      { name: "tags", values: tags.split(",") },
    ]),
  ).toString()

  // Tag our response with the keyword so we can query by it later.
  // Tag our response with the page number so we can sort them.
  const okType = mediaSearchResponse.transform((response) => ({
    ...response,
    keyword,
    assetType,
    uploaderId,
    tags,
    page,
  }))

  const started = performance.now()

  return request(`${requestUrl?.href ?? ""}?${query}`, {
    headers: {
      repository,
      ...assetApiHeaders,
    },
    credentials: getRequestCredentials(authStrategy),
    okType,
  }).then((result) => ({
    ...result,
    time: (performance.now() - started).toFixed(0),
  }))
}

/**
 * There's no need to revalidate search results which will drift overtime by design as the scores of assets change.
 * There's no need to revalidate non search results either as we manually revalidate these with useMediaLibraryCacheInvalidation.
 */
cache.registerFetcherConfig(searchMedia, {
  revalidationStrategy: { type: "never" },
})

interface UseMediaLibrarySearchArgs {
  initialAssetType?: MediaAssetType
  mode: "select" | "browse"
}

export interface MediaLibraryFilters {
  keyword: string
  assetType: MediaAssetType
  uploaderId?: string
  tagIds: string[]
}

export const defaultMediaLibraryFilters: MediaLibraryFilters = {
  keyword: "",
  assetType: "all",
  uploaderId: undefined,
  tagIds: [],
}

export function useMediaSearch(args: UseMediaLibrarySearchArgs) {
  const { initialAssetType = defaultMediaLibraryFilters.assetType, mode } = args

  const config = useEditorConfig()

  const [searchState, setSearchState] = useState<SearchState>({
    ...defaultMediaLibraryFilters,
    assetType: initialAssetType,
    page: 0,
  })

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { assetsApiBaseUrl, authStrategy, repository } = config
  const { page, cursor, keyword, assetType, uploaderId, tagIds } = searchState

  const lastResponse = useRequest(
    searchMedia,
    searchMediaArgs({
      assetsApiBaseUrl,
      authStrategy,
      repository,
      keyword,
      assetType,
      uploaderId,
      tagIds,
      page,
      cursor,
    }),
  )

  useMediaLibraryAnalyticsMediaSearch({
    assetType,
    keyword,
    tagIds,
    mode,
  })

  const media = useMedia(keyword, assetType, uploaderId, tagIds)

  const hasNextPage = lastResponse.total !== media.length

  function fetchNextPage() {
    if (lastResponse.cursor === undefined) return

    setSearchState((current) => ({
      ...current,
      page: current.page + 1,
      cursor: lastResponse.cursor,
    }))
  }

  /**
   * This adds a new media to all of the cache records.
   */
  function unshiftMedia(newMedia: MediaAsset[]) {
    setSearchState((current) => ({
      ...current,
      page: 0,
      cursor: undefined,
    }))

    updateMediaCache((items, meta) => {
      // Delete all but our first page from cache as adding more items to the front will shift all other pages, making them obsolete.
      if (meta.page !== 0) return undefined
      return [...newMedia, ...items]
    })
  }

  function setFilters(filters: Partial<MediaLibraryFilters>) {
    // Always reset the page to 0 when updating a filter, as we want to see
    // the first page fully without immediately loading more than that.
    setSearchState((current) => ({
      ...current,
      ...filters,
      page: 0,
      cursor: undefined,
    }))
  }

  /**
   * Reset all filters to their default values.
   * All filters means: keyword, asset type, and tags.
   */
  function resetFilters() {
    setSearchState((current) => ({
      ...current,
      ...defaultMediaLibraryFilters,
      assetType:
        // We reset the asset type filter only when it is not initially set to a specific type.
        initialAssetType === "all"
          ? defaultMediaLibraryFilters.assetType
          : current.assetType,
      page: 0,
      cursor: undefined,
    }))
  }

  return {
    keyword,
    assetType,
    uploaderId,
    tagIds,
    setFilters,
    resetFilters,
    media,
    mediaTotal: lastResponse.total,
    limit,
    hasNextPage,
    fetchNextPage,
    updateMedia,
    unshiftMedia,
    deleteMedia,
    deleteSearchData,
    revalidateFirstPage,
  }
}
/**
 * Function to update media items in the cache and ensure
 * that the shown data is up to date.
 */
function updateMedia(
  newFiles: { id: string; response: Partial<MediaAsset> | undefined }[],
) {
  updateMediaCache((items) => {
    return items.flatMap((mediaItem) => {
      const newMediaItem = newFiles.find((file) => file.id === mediaItem.id)
      // If the media item is not found in the new files, it means it was not updated and should be kept
      if (!newMediaItem) return mediaItem
      // If the response is undefined, it means the media item was unsuccessful and should be removed from the cache
      if (newMediaItem.response === undefined) return []
      // If the response is defined, it means the media item was successful and should be updated with the server response
      // The update can be partial as it's safer for optimistic client updates.
      return { ...mediaItem, ...newMediaItem.response }
    })
  })
}

function deleteMedia(mediaIds: string[]) {
  updateMediaCache((items) =>
    items.filter((media) => !mediaIds.includes(media.id)),
  )
}

function deleteSearchData(args: MediaLibraryFilters) {
  const { keyword, assetType, uploaderId, tagIds } = args
  const tagsString = tagIds?.join(",")

  updateFetcherData(searchMedia, (body) => {
    if (
      body.keyword === keyword &&
      body.assetType === assetType &&
      body.uploaderId === uploaderId &&
      body.tags === tagsString
    ) {
      return undefined
    }
    return body
  })
}

type MediaPage = Awaited<ReturnType<typeof searchMedia>>

export function getMedia(
  allPages: MediaPage[],
  keyword: string,
  assetType: MediaAssetType,
  uploaderId: string | undefined,
  tags: string,
) {
  const relevantPages = allPages.filter(
    (page) =>
      page.keyword === keyword &&
      page.assetType === assetType &&
      page.uploaderId === uploaderId &&
      page.tags === tags,
  )

  const sortedPages = sort(relevantPages, (page) => page.page)
  const medias = sortedPages.flatMap((page) => page.items)

  /**
   * Different pages of the same search request can naturally contain duplicates.
   * Remove those duplicates to make the UI looks less weird and React happy.
   * Example:
   * 1) Search for "dog"
   * 2) Get results ranging from _score 3000 to 2000
   * 3) The current user or a collaborator modifies the first result media's meta from "dog" to "hotdog"
   *    and now its _score relative to the current search session is only 1000
   * 4) Scroll and ask for the second page: We get that same result media back.
   */
  return keyword ? uniqBy(medias, (media) => media.id) : medias
}

function useMedia(
  keyword: string,
  assetType: MediaAssetType,
  uploaderId: string | undefined,
  tagIds: string[],
) {
  const allPages = useFetcherData(searchMedia)
  return getMedia(allPages, keyword, assetType, uploaderId, tagIds.join(","))
}

interface PageMetadata {
  page: number
  assetType: MediaAssetType
  keyword: string
}

type UpdateMediaFunction = (
  currentItems: MediaAsset[],
  metadata: PageMetadata,
) => MediaAsset[] | undefined

export function updateMediaCache(updateFunction: UpdateMediaFunction) {
  updateFetcherData(searchMedia, (body) => {
    const { items, total } = body

    const newItems = updateFunction(items, body)
    if (!newItems) return undefined

    return {
      ...body,
      total: total + (newItems.length - items.length),
      items: newItems,
    }
  })
}

interface RevalidateFirstPageArgs {
  config: EditorConfig
  keyword?: string
  assetType?: MediaAssetType
  uploaderId?: string
  tagIds?: string[]
}

/**
 * Revalidates the first page of the media library immediately.
 */
function revalidateFirstPage(args: RevalidateFirstPageArgs) {
  const {
    config,
    keyword = defaultMediaLibraryFilters.keyword,
    assetType = defaultMediaLibraryFilters.assetType,
    tagIds = defaultMediaLibraryFilters.tagIds,
  } = args
  if (!config.mediaLibrary) throw new Error("media library is not configured")

  const { assetsApiBaseUrl, authStrategy, repository } = config

  void revalidateData(
    searchMedia,
    searchMediaArgs({
      assetsApiBaseUrl,
      repository,
      authStrategy,
      keyword,
      assetType,
      tagIds,
      page: 0,
    }),
  )
}

/**
 * Function to remove media items from cache and ensure that the shown data
 * is up to date.
 *
 * Clear all media library cache but the first page.
 *
 * Then the first page will be revalidated to be in sync with the server data.
 * By not removing the first page and then revalidating we can avoid unnecessary
 * loading states while relying on the cached data.
 */
export function clearMediaCacheAndRevalidateFirstPage(
  args: RevalidateFirstPageArgs,
) {
  const {
    config,
    keyword = defaultMediaLibraryFilters.keyword,
    assetType = defaultMediaLibraryFilters.assetType,
    tagIds = defaultMediaLibraryFilters.tagIds,
  } = args

  if (!config.mediaLibrary) throw new Error("media library is not configured")

  // Clear all media library cache but the first page
  updateFetcherData(searchMedia, (body) => (body.page === 0 ? body : undefined))

  // Revalidate the first page
  revalidateFirstPage({ config, keyword, assetType, tagIds })
}

interface SearchState {
  // The search keyword/term
  keyword: string
  // The asset type to filter by
  assetType: MediaAssetType
  // The uploader (userId) to filter by
  uploaderId?: string
  // The tags to filter by
  tagIds: string[]
  // The current max loaded page
  page: number
  // The server pagination cursor used to load the next page
  cursor?: string
}

export interface SearchMediaArgs {
  assetsApiBaseUrl: URL | undefined
  repository: string
  authStrategy: AuthStrategy
  keyword: string
  assetType: MediaAssetType
  uploaderId?: string
  tagIds: string[]
  page: number
  cursor?: string
}

export function searchMediaArgs(
  args: SearchMediaArgs,
): Parameters<typeof searchMedia> {
  return [
    args.assetsApiBaseUrl,
    args.repository,
    args.authStrategy,
    args.keyword,
    args.assetType,
    args.uploaderId,
    args.tagIds.join(","),
    args.page,
    args.cursor,
  ]
}
